import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { TextInput } from '@/components/inputs/text-input';
import { Form } from '@/components/form';
import { Button } from '@/components/button';
import { PharmacyListCard } from '@/components/encounter/pharmacy/pharmacy-list-card';
import { useState, Fragment, useEffect, useCallback } from 'react';
import { useGetPharmacies } from '@/api/get-pharmacies';
import {
  GetPersonalInfoResponse,
  GetZipFromCoordinatesErrorType,
  PharmaciesSort,
  PharmacyType,
} from '@/api/api.schemas';
import { PAGE_SIZE_OPTIONS, PHARMACY_TYPES } from '@/util/constants';
import { Section } from '@/components/containers/section';
import { useEncounter } from '@/contexts/encounter-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import { EmptyList } from '@/components/containers/empty-list';
import { Pagination } from '@/components/pagination';
import clsx from 'clsx';
import { useUpdateEncountersPreferencesPharmacyType } from '@/api/update-preferred-pharmacy-type';
import { useGetPatient } from '@/api/get-personal-info';
import { useLoggedUser } from '@/contexts/current-user-context';
import { PharmacySort } from '@/components/encounter/pharmacy/pharmacy-sort';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './pharmacy-list.module.css';
import { useGetZipCodes } from '@/api/get-zip-from-coordinates';
import { ErrorField } from '@/components/error-field';
import { Loader } from '@/components/loaders/loader';
import { Banner } from '@/components/banner';
import { TabSet } from '@/components/containers/tab-set';
import { useAsyncAction } from '@/hooks/use-async-action';
import { Navigate } from '@/util/react-router-dom';
import { optionalZipCode } from '@/util/validators';
import { Separator } from '@/components/separator';
import { PharmacySelectionDescription } from './pharmacy-selection-description';
import { useTranslation } from '@/contexts/i18n-context';

const translationBase = 'visit.pharmacySelection';

interface Coordinates {
  latitude: number;
  longitude: number;
}

const schema = z.object({
  zipCode: optionalZipCode(),
  name: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface PharmacyListProps {
  onPageChange: () => void;
}

export function PharmacyList({ onPageChange }: PharmacyListProps) {
  const { user } = useLoggedUser();
  const { data: patient, isFetching } = useGetPatient(user.id);

  if (isFetching) {
    return (
      <Section>
        <Loader type="small" />
      </Section>
    );
  }

  if (!patient) {
    return <Navigate to="/system-error" />;
  }

  return <PharmacyListForm patient={patient} onPageChange={onPageChange} />;
}

interface PharmacyListFormProps {
  patient: GetPersonalInfoResponse;
  onPageChange: () => void;
}

export function PharmacyListForm({ patient, onPageChange }: PharmacyListFormProps) {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const isMobile = useMediaQuery('--screen-md');

  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [location, setLocation] = useState<Coordinates>();
  const [error, setError] = useState<string>();
  const [page, setPage] = useState(1);
  const [pharmacyType, setPharmacyType] = useState<PharmacyType>(
    encounter.preferredPharmacyType ?? PHARMACY_TYPES[0],
  );

  const isMailOrder = pharmacyType === PharmacyType.MailOrder;

  const [sort, setSort] = useState<PharmaciesSort>(getDefaultSorting(pharmacyType));
  const [pageSize, setPageSize] = useState(Number(PAGE_SIZE_OPTIONS[0]));
  const [filters, setFilters] = useState<FormValues>({
    zipCode: isMailOrder ? '' : (patient?.zip ?? ''),
  });

  const { mutateAsync: setPreferredPharmacyType } = useUpdateEncountersPreferencesPharmacyType();
  const { data: zipCodeFromCurrentLocation, error: zipCodeError } = useGetZipCodes(
    { lat: location?.latitude, lng: location?.longitude },
    {
      query: {
        enabled: !!location,
      },
    },
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      zipCode: isMailOrder ? '' : (patient?.zip ?? ''),
    },
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      onPageChange();
    },
    [setPage, onPageChange],
  );

  const handleSortChange = (newSort: PharmaciesSort) => {
    setSort(newSort);
    handlePageChange(1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    handlePageChange(1);
  };

  const { data: pharmaciesData, isFetching: isFetchingPharmacies } = useGetPharmacies({
    specialty: pharmacyType,
    name: filters.name,
    zip: filters.zipCode,
    sort,
    pageSize,
    page,
    ...(zipCodeFromCurrentLocation ? location : {}),
  });

  const { trigger: handlePharmacyTypeChange } = useAsyncAction(async (type: PharmacyType) => {
    setPharmacyType(type);
    if (encounter.preferredPharmacyType !== type) {
      setSort(getDefaultSorting(type));
      form.reset({ zipCode: '', name: '' });
      setFilters(form.getValues());
      await setPreferredPharmacyType({
        encounterId: encounter.id,
        data: { pharmacyType: type },
      });
      await invalidateEncounter();
    }
    handlePageChange(1);
  });

  const onSubmit = (data: FormValues) => {
    handlePageChange(1);
    setFilters(data);
  };

  useEffect(() => {
    if (zipCodeFromCurrentLocation?.zipCode && !zipCodeError) {
      form.setValue('zipCode', zipCodeFromCurrentLocation.zipCode);
      setFilters(form.getValues());

      // We set the sort of the pharmacies to be by distance
      setSort(PharmaciesSort.Distance);
      handlePageChange(1);
      return;
    }

    if (zipCodeError) {
      setError(
        zipCodeError.type === GetZipFromCoordinatesErrorType.ZipNotInUS
          ? `${translationBase}.error.notInUs`
          : 'common.errors.unknown',
      );
      setLocation(undefined);
    }
  }, [zipCodeFromCurrentLocation, location, zipCodeError, form, handlePageChange]);

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      return;
    }

    setIsGettingLocation(true);
    setError(undefined);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords: Coordinates = {
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
        };

        setLocation(coords);
        setIsGettingLocation(false);
      },
      () => setIsGettingLocation(false),
    );
  };

  const isListEmpty =
    !isFetchingPharmacies && pharmaciesData?.pharmacies && pharmaciesData.pharmacies.length === 0;
  const isFetchingOrEmpty = isFetchingPharmacies || isListEmpty;

  const showDistanceWarning =
    sort === PharmaciesSort.Distance &&
    pharmaciesData?.totalCount !== 0 &&
    !!pharmaciesData?.pharmacies.every((pharmacy) => !pharmacy.distance);

  return (
    <Section>
      <PharmacySelectionDescription className={styles.pharmacySelectionDescription} />
      <Form form={form} onSubmit={onSubmit}>
        <div className={styles.filtersContainer}>
          <TabSet
            tabs={PHARMACY_TYPES.map((type) => ({
              text: `${translationBase}.filters.options.${type}`,
              onClick: () => handlePharmacyTypeChange(type),
              isSelected: pharmacyType === type,
            }))}
            className={styles.tabSet}
          />
          <div className={styles.filtersForm}>
            <div className={styles.filters}>
              {!isMailOrder && (
                <>
                  <TextInput
                    withIconsIndicators={false}
                    withErrorMessage={false}
                    className={styles.filterTextInput}
                    inputClassName={clsx(styles.filterInput, 'body-3')}
                    icon="pin_drop"
                    aria-label={t(`${translationBase}.filters.zipCodeLabel`)}
                    name="zipCode"
                    placeholder={t(`${translationBase}.filters.zipCodePlaceholder`)}
                  />
                  <div className={styles.verticalSeparator} />
                </>
              )}
              <TextInput
                withIconsIndicators={false}
                className={styles.filterTextInput}
                inputClassName={clsx(styles.filterInput, 'body-3')}
                icon="pill"
                aria-label={t(`${translationBase}.filters.nameLabel`)}
                name="name"
                placeholder={t(`${translationBase}.filters.namePlaceholder`)}
              />
              <Button type="submit" className={styles.searchButton} leftIcon="search">
                {isMobile && <span>{t(`${translationBase}.filters.search`)}</span>}
              </Button>
            </div>
            {/* In this particular case, we want to show the error bellow the filters form row and not bellow the input itself */}
            {!isMailOrder && form.formState.errors.zipCode && (
              <ErrorField
                error={form.formState.errors.zipCode}
                {...(isMobile ? { className: 'body-2' } : {})}
              />
            )}
          </div>
          {(!isMailOrder || !isFetchingOrEmpty) && (
            <div className={styles.filterContainer}>
              <div className={styles.sortContainer}>
                {!isMailOrder && !isFetchingOrEmpty && (
                  <div className={styles.closestToMeContainer}>
                    <Button
                      variant="outlined"
                      size={isMobile ? 'L' : 'S'}
                      leftIcon="pin_drop"
                      onClick={handleGetCurrentLocation}
                      disabled={isGettingLocation}
                    >
                      {t(`${translationBase}.showPharmaciesCloseToMe`)}
                    </Button>
                    {isMobile && error && (
                      <ErrorField error={{ type: 'validate', message: error }} />
                    )}
                  </div>
                )}
                {!isFetchingOrEmpty && (
                  <PharmacySort
                    sort={sort}
                    handleSortChange={handleSortChange}
                    isMailOrder={isMailOrder}
                  />
                )}
              </div>
              {!isMobile && error && <ErrorField error={{ type: 'validate', message: error }} />}
            </div>
          )}
        </div>
      </Form>

      {showDistanceWarning && (
        <Banner className={styles.distanceWarning} type="warning">
          <span>{t(`${translationBase}.error.noDistance`)}</span>
        </Banner>
      )}

      <div className={clsx(styles.pharmacyList, isFetchingOrEmpty && styles.emptyOrLoadingState)}>
        {isFetchingPharmacies ? (
          <Loader className={styles.loader} type="small" />
        ) : (
          <>
            {pharmaciesData?.pharmacies.map((pharmacy, index) => (
              <Fragment key={pharmacy.id}>
                <PharmacyListCard key={pharmacy.id} pharmacy={pharmacy} />
                {index != pharmaciesData.pharmacies.length - 1 && (
                  <Separator className={styles.separator} />
                )}
              </Fragment>
            ))}
            {isListEmpty && <EmptyList title={`${translationBase}.emptyState.title`} />}
          </>
        )}
      </div>

      <Form form={form} onSubmit={onSubmit}>
        {!isFetchingOrEmpty && (
          <Pagination
            label={t(`${translationBase}.pagination.label`)}
            readersOnlyLabel
            pageSize={pageSize}
            handlePageSizeChange={handlePageSizeChange}
            pageSizeTxt={`${translationBase}.pagination.title`}
            pages={
              pharmaciesData?.totalCount
                ? Math.ceil(pharmaciesData?.totalCount / Number(pageSize))
                : 1
            }
            currentPage={page}
            onPageChange={(page) => handlePageChange(page)}
          />
        )}
      </Form>
    </Section>
  );
}

function getDefaultSorting(pharmacyType: PharmacyType) {
  return pharmacyType === PharmacyType.MailOrder ? PharmaciesSort.NameAsc : PharmaciesSort.Distance;
}
